/**
 * Техкарты-варианты сезона — слой над `/api/v1/planning/tech-cards/*`
 * (backend PR #764, ADR-066).
 *
 * Экран собран по УТВЕРЖДЁННОМУ founder макету
 * `docs/superpowers/prototypes/annual-planning/v2-05-techcard.html`
 * (ветка `docs/wireframes-season-planning-v2`, скриншот рядом).
 *
 * Что обещает пользователю этот слой:
 *   • вариант техкарты — атом бюджета: у культуры их несколько («Интенсив»,
 *     «Базовый», «Эконом-защита»), задача макета — «2-4 варианта за час»,
 *     поэтому клонирование стоит рядом со списком;
 *   • блок (обработка почвы / сев / питание / защита / уборка) — сменный
 *     набор операций. Замена блока сначала СЧИТАЕТСЯ (`dry_run`) и
 *     показывается как «было → станет → дельта», и только потом применяется —
 *     либо в текущий вариант, либо в новый (`as_new_card_name`);
 *   • ₽/га не хранится в БД (ADR-066 D4), а считается сервисом — поэтому в
 *     ответах он приходит вместе с картой, а не запрашивается отдельно.
 *
 * Язык интерфейса — агронома и экономиста: «вариант техкарты», «блок»,
 * «агроокно», «своя / наём». Модельные слова (block_kind, dry_run, basis)
 * наружу не отдаём.
 */
import { formatPrice } from './money';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { describeApiError } from '@/lib/apiError';
import { cropLabel, type CropLike } from '@/lib/cropLabel';
import {
  cropStructureKeys,
  planVariantKeys,
  sppOverviewKeys,
  techCardKeys,
  variantListKeys,
} from '@/lib/planning/queryKeys';
import { toast } from '@/lib/toast';

// ── Блоки ────────────────────────────────────────────────────────────────────

export type BlockKind =
  | 'tillage'
  | 'sowing'
  | 'nutrition'
  | 'protection'
  | 'harvest'
  | 'transport'
  | 'other';

/** Порядок блоков в карте — как в макете (от обработки почвы к уборке). */
export const BLOCK_ORDER: readonly BlockKind[] = [
  'tillage',
  'sowing',
  'nutrition',
  'protection',
  'harvest',
  'transport',
  'other',
];

/** Названия блоков — зеркало `BLOCK_TITLES` бэкенда (язык агронома). */
export const BLOCK_TITLES: Record<BlockKind, string> = {
  tillage: 'Обработка почвы',
  sowing: 'Сев и сорт',
  nutrition: 'Система питания',
  protection: 'Система защиты',
  harvest: 'Уборка',
  transport: 'Транспорт и подвоз',
  other: 'Прочее',
};

export function blockTitle(kind: string | null | undefined): string {
  if (!kind) return 'Без блока';
  return BLOCK_TITLES[kind as BlockKind] ?? 'Прочее';
}

// ── Типы ответа ──────────────────────────────────────────────────────────────

export interface TechCardMaterial {
  id: string;
  budget_item_id: string;
  budget_item_name: string | null;
  /** Секция бюджетной позиции — она же статья затрат (ADR-071 D8); по ней
   *  же диалог понимает, что позиция семенная. */
  budget_item_section?: string | null;
  planned_price: number | null;
  rate: number;
  rate_unit_id: string | null;
  rate_unit_symbol?: string | null;
  rate_basis: 'per_ha' | 'per_ton' | 'per_unit';
  /** Доля площади У МАТЕРИАЛА (ADR-071 D5) — своя, независимая от доли операции. */
  area_share_pct?: number;
  /** Сорт/гибрид семенной позиции — требование founder к диалогу сева. */
  variety_id?: string | null;
  variety_name?: string | null;
  /** Густота посева на гектар и в чём она (шт / тыс. шт / млн шт). */
  seeding_density?: number | null;
  seeding_density_unit_id?: string | null;
  seeding_density_unit_symbol?: string | null;
  seq: number;
  notes: string | null;
}

/** Строка ФОТ по роли (ADR-071 D11): расценка на одного человека. */
export interface TechCardLaborRow {
  id?: string;
  labor_role: 'operator' | 'helper';
  persons_per_unit: number;
  pay_form: 'piece' | 'hourly';
  /** null = базовая расценка из норматива сезона (справочника расценок). */
  base_rate: number | null;
  rate_basis: string | null;
  bonus_rate: number | null;
  bonus_pct: number | null;
  hazard_pct: number;
  payroll_tax_pct: number;
  notes?: string | null;
}

export const LABOR_ROLE_TITLES: Record<TechCardLaborRow['labor_role'], string> = {
  operator: 'Тракторист / машинист',
  helper: 'Прицепщик / разнорабочий',
};

export const PAY_FORM_TITLES: Record<TechCardLaborRow['pay_form'], string> = {
  piece: 'сдельная',
  hourly: 'повременная',
};

/** Прямая сумма по статье на строке (ADR-071 D5) — замещает расчётную часть. */
export interface TechCardDirectCost {
  id?: string;
  article: CostArticle;
  amount: number;
  notes?: string | null;
}

/** Статьи затрат D8 — порядок как в шапке эталона Ольховатки. */
export type CostArticle =
  | 'payroll'
  | 'seed'
  | 'fertilizer'
  | 'pesticide'
  | 'other_tmc'
  | 'fuel'
  | 'outsource';

export const ARTICLE_ORDER: readonly CostArticle[] = [
  'payroll',
  'seed',
  'fertilizer',
  'pesticide',
  'other_tmc',
  'fuel',
  'outsource',
];

export const ARTICLE_TITLES: Record<CostArticle, string> = {
  payroll: 'Оплата труда',
  seed: 'Семена',
  fertilizer: 'Удобрения',
  pesticide: 'СЗР',
  other_tmc: 'Прочие ТМЦ',
  fuel: 'ГСМ',
  outsource: 'Наёмные работы',
};

export interface TechCardOperation {
  id: string;
  tech_card_id: string;
  parent_operation_id: string | null;
  /** Этап карты (ADR-071 D3) — пользовательская группировка со своими именами. */
  stage_id?: string | null;
  /** Доля обрабатываемой площади, % (ADR-071 D5). 100 = весь клин. */
  area_share_pct?: number;
  seq: number;
  block_kind: string | null;
  block_label: string | null;
  company_op_type_id: string;
  op_type_name: string | null;
  name: string | null;
  window_starts_on: string | null;
  window_ends_on: string | null;
  window_work_days: number | null;
  multiplicity: number;
  volume_basis: 'per_ha' | 'per_ton' | 'fixed';
  volume: number | null;
  execution_kind: 'own' | 'outsourced' | 'hired_labor';
  machinery_unit_id: string | null;
  machinery_count: number | null;
  throughput_norm: number | null;
  throughput_basis: string | null;
  fuel_item_id: string | null;
  fuel_rate: number | null;
  fuel_rate_basis: string | null;
  /** ГСМ свой/чужой (ADR-071 D5). null = выводится из «кто выполняет». */
  fuel_ownership?: 'own' | 'provider' | null;
  /** Коэффициент накладных на топливо (перегоны, развороты) — только к базе «на га». */
  fuel_overhead_coef?: number;
  /** Плечо перевозки, км (лестница: строка ?? карта ?? хозяйство). */
  haul_distance_km?: number | null;
  /** Грузоподъёмность рейса, т — знаменатель числа рейсов. */
  haul_capacity_t?: number | null;
  /** Позиция наёмной работы в бюджетном справочнике. */
  outsource_item_id?: string | null;
  outsource_rate_basis?: 'per_unit' | 'per_ton_km';
  /** СОБСТВЕННАЯ доля площади наёмной услуги (ADR-071 D6-d п.9). */
  outsource_share_pct?: number;
  /**
   * НЗП вручную (ADR-070): `null` — отнесение по календарю (год начала
   * агроокна раньше года сезона карты), `true`/`false` — задано рукой.
   */
  wip_override?: boolean | null;
  /** НЗП — осенние работы под этот урожай, выполненные в прошлом календарном году. */
  is_wip?: boolean;
  notes: string | null;
  materials?: TechCardMaterial[];
  /** Строки ФОТ по ролям (ADR-071 D11) — приходят с деталью карты. */
  labor?: TechCardLaborRow[];
  /** Прямые суммы по статьям (ADR-071 D5) — приходят с деталью карты. */
  direct_costs?: TechCardDirectCost[];
}

export interface OperationCost {
  operation_id: string;
  block_kind: string | null;
  is_wip?: boolean;
  materials: number;
  fuel: number;
  labor: number;
  outsource: number;
  total: number;
  /** Деньги строки в абсолюте (ADR-071 D6-b) — «деньги по каждой строке» эталона. */
  total_abs?: number;
  /** Абсолютные ₽ строки по статьям D8 (только ненулевые). */
  by_article?: Partial<Record<CostArticle, number>>;
  /** Объём работ строки (D6-c) — вычисленный либо заданный вручную. */
  volume?: number | null;
  fuel_litres?: number | null;
  /** Чего не хватило для расчёта ЭТОЙ строки. */
  gaps?: string[];
}

/** Статья затрат в трёх измерениях (ADR-071 D8) — как подвал эталона. */
export interface ArticleMoney {
  total: number;
  per_ha: number | null;
  per_ton: number | null;
}

export interface CardCost {
  per_ha: number;
  by_block: Record<string, number>;
  total: number | null;
  /** Себестоимость тонны по ФИЗИЧЕСКОМУ весу — основная цифра (ADR-071 D7). */
  cost_per_ton: number | null;
  /** Товарная себестоимость (зачётный вес) — дополнительная. */
  marketable_cost_per_ton?: number | null;
  area_ha: number | null;
  /** 'assigned' — считалось на площадь назначения; 'per_ha_unit' — площади нет. */
  basis_area?: 'assigned' | 'per_ha_unit';
  yield_c_ha: number | null;
  /** Блок продукции D7: валовка в физвесе и товарная. */
  gross_t?: number | null;
  marketable_gross_t?: number | null;
  /** Статьи затрат D8 (всего / на га / на тонну). */
  by_article?: Partial<Record<CostArticle, ArticleMoney>>;
  /** Свёртка статей в «виды затрат» #803, ₽ абсолют. */
  by_kind?: Record<string, number>;
  /** ₽/га НЗП-строк карты — «в т.ч. работы прошлого календарного года». */
  wip_per_ha?: number;
  /** Чего не хватило для честного ₽/га — показываем текстом, не прячем. */
  gaps: string[];
  operations: OperationCost[];
}

export type TechCardStatus = 'draft' | 'ready' | 'archived';

export const CARD_STATUS_TITLES: Record<TechCardStatus, string> = {
  draft: 'Черновик',
  ready: 'Готова',
  archived: 'Убрана из сезона',
};

/** Этап карты (ADR-071 D3): свободное имя + порядок. Не путать с block_kind. */
export interface TechCardStage {
  id: string;
  tech_card_id: string;
  name: string;
  seq: number;
  notes: string | null;
}

export interface TechCard {
  id: string;
  company_id: string;
  farm_id: string | null;
  season_id: string;
  crop_id: string;
  growing_goal_id: string | null;
  /**
   * ПРОИЗВОДНОЕ (ADR-071 D2): единственный элемент множества либо null.
   * Правда — в `predecessor_crop_ids`.
   */
  predecessor_crop_id: string | null;
  /** Множество предшественников; пустое = общая карта культуры (ADR-071 D2). */
  predecessor_crop_ids?: string[];
  /** Этапы карты — приходят с деталью (`GET /tech-cards/{id}`). */
  stages?: TechCardStage[];
  name: string;
  target_yield_c_ha: number | null;
  marketable_yield_c_ha: number | null;
  based_on_tech_card_id: string | null;
  status: TechCardStatus;
  notes: string | null;
  archived_at: string | null;
  scenarios_count: number;
  operations: TechCardOperation[];
  cost: CardCost | null;
}

export interface BlockReplaceResult {
  tech_card_id: string;
  block_kind: string;
  block_title: string;
  /** `false` — это предпросмотр: в БД ещё ничего не изменилось. */
  applied: boolean;
  before: CardCost;
  after: CardCost;
  delta_per_ha: number;
}

// ── Ключи ────────────────────────────────────────────────────────────────────

// Живут в `queryKeys.ts` — их делят техкарты и экраны варианта плана; здесь
// переэкспорт, чтобы потребители не переучивали импорт.
export { planVariantKeys, techCardKeys };

// ── Чтение ───────────────────────────────────────────────────────────────────

export function useTechCards(seasonId: string | null, includeArchived = false) {
  return useQuery<TechCard[], Error>({
    queryKey: techCardKeys.list(seasonId, includeArchived),
    queryFn: () => {
      const qs = new URLSearchParams({ season_id: seasonId as string });
      if (includeArchived) qs.set('include_archived', 'true');
      return api.get<TechCard[]>(`/api/v1/planning/tech-cards?${qs.toString()}`);
    },
    enabled: !!seasonId,
    staleTime: 30_000,
  });
}

export function useTechCard(cardId: string | null) {
  return useQuery<TechCard, Error>({
    queryKey: techCardKeys.card(cardId),
    queryFn: () => api.get<TechCard>(`/api/v1/planning/tech-cards/${cardId}`),
    enabled: !!cardId,
    staleTime: 30_000,
  });
}

/**
 * Предпросмотр замены блока — «что станет с вариантом, если применить».
 *
 * Это POST с `dry_run: true`: бэк ничего не пишет, считает слияние строк в
 * памяти и возвращает «было / стало / дельта». Держим его запросом, а не
 * мутацией, чтобы повторный выбор того же источника не бил в сеть — агроном
 * щёлкает вариантами туда-сюда, сравнивая цифры.
 */
export function useBlockReplacePreview(
  cardId: string | null,
  blockKind: BlockKind | null,
  sourceCardId: string | null,
) {
  const enabled = !!cardId && !!blockKind && !!sourceCardId;
  return useQuery<BlockReplaceResult, Error>({
    queryKey: techCardKeys.preview(cardId ?? '', blockKind ?? '', sourceCardId ?? ''),
    queryFn: () =>
      api.post<BlockReplaceResult>(
        `/api/v1/planning/tech-cards/${cardId}/blocks/${blockKind}/replace`,
        { source_tech_card_id: sourceCardId, dry_run: true },
      ),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

// ── Запись ───────────────────────────────────────────────────────────────────

/**
 * Что протухает после правки техкарты или назначения.
 *
 * Гасим ОБА неймспейса — и техкарты, и вариант плана. Экран назначения и
 * сводка целиком читаются из `planVariantKeys.summary(...)`, и без второй
 * строки «Назначить» отрабатывала бы вхолостую: тост есть, а строка, покрытие,
 * экономика и плашка «расчёт устарел» остаются прежними до F5 (ревью head-web
 * по #803). Обратное направление (`useCopyAssignments`, `useRecalculateVariant`)
 * симметрично гасит техкарты — асимметрия и была источником дефекта.
 */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: techCardKeys.all() });
    void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
  };
}

/**
 * Отказ бэка обязан быть виден там, где сделано действие. Замена блока и
 * клонирование идут из панели без места под текст ошибки, поэтому 409/422/403
 * показываем тостом — иначе кнопка «сработала», а карта осталась прежней.
 */
function notifyFailure(err: unknown): void {
  toast.error(techCardErrorText(err));
}

export interface BlockReplaceInput {
  cardId: string;
  blockKind: BlockKind;
  sourceCardId: string;
  /** Задано — «Сохранить как новый вариант»: текущий вариант не меняется. */
  asNewCardName?: string | null;
}

export function useApplyBlockReplace() {
  const invalidate = useInvalidate();
  return useMutation<BlockReplaceResult, Error, BlockReplaceInput>({
    mutationFn: ({ cardId, blockKind, sourceCardId, asNewCardName }) =>
      api.post<BlockReplaceResult>(
        `/api/v1/planning/tech-cards/${cardId}/blocks/${blockKind}/replace`,
        {
          source_tech_card_id: sourceCardId,
          dry_run: false,
          // Пустую строку не шлём: бэк примет её за имя и создаст вариант без
          // названия. Нет имени — значит правим текущий вариант.
          ...(asNewCardName?.trim() ? { as_new_card_name: asNewCardName.trim() } : {}),
        },
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/**
 * Правка шапки варианта. Нужна экрану назначения: вариант, заведённый под
 * другой предшественник, назначается строке плана через копию — и у копии
 * предшественник надо проставить, иначе копия снова «общая по культуре».
 */
export interface PatchCardInput {
  cardId: string;
  patch: {
    name?: string;
    predecessor_crop_id?: string | null;
    growing_goal_id?: string | null;
    target_yield_c_ha?: number | null;
    status?: TechCardStatus;
  };
}

export function usePatchTechCard() {
  const invalidate = useInvalidate();
  return useMutation<TechCard, Error, PatchCardInput>({
    mutationFn: ({ cardId, patch }) => api.patch<TechCard>(`/api/v1/planning/tech-cards/${cardId}`, patch),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/**
 * Заведение первой техкарты культуры. Клонирование (`useCloneTechCard`) — про
 * ВТОРОЙ вариант той же культуры; первый заводить было нечем, и пустой сезон
 * упирался в текст без кнопки (founder UAT 19.08).
 */
export interface CreateCardInput {
  seasonId: string;
  cropId: string;
  name: string;
  predecessorCropId?: string | null;
  targetYieldCHa?: number | null;
}

export function useCreateTechCard() {
  const invalidate = useInvalidate();
  return useMutation<TechCard, Error, CreateCardInput>({
    mutationFn: ({ seasonId, cropId, name, predecessorCropId, targetYieldCHa }) =>
      api.post<TechCard>('/api/v1/planning/tech-cards', {
        season_id: seasonId,
        crop_id: cropId,
        name: name.trim(),
        predecessor_crop_id: predecessorCropId ?? null,
        target_yield_c_ha: targetYieldCHa ?? null,
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

// ── Мастер создания техкарты (ADR-071 D1, 8 шагов founder) ──────────────────

export interface WizardPreview {
  auto_area_ha: number;
  fields_count: number;
  rows: {
    crop_id: string;
    predecessor_crop_id: string | null;
    predecessor_name: string | null;
    fields_count: number;
    area_ha: number;
    exact_predecessor: boolean;
  }[];
  fields: {
    id: string;
    name: string;
    area_ha: number;
    predecessor_crop_id: string | null;
    predecessor_name: string | null;
  }[];
  gaps: string[];
}

/**
 * «Подходит N га · M полей» ДО создания карты — шаг предпросмотра мастера.
 *
 * Считается так, как БУДЕТ после создания: будущая карта участвует в
 * конкуренции «точная бьёт общую» с уже назначенными. Пустой ответ — честный
 * ноль с причиной в `gaps`, а не мусорная цифра.
 */
export function useWizardPreview(
  scenarioId: string | null,
  cropId: string | null,
  predecessorCropIds: string[],
) {
  const preds = [...predecessorCropIds].sort();
  return useQuery<WizardPreview, Error>({
    queryKey: [...techCardKeys.all(), 'wizard-preview', scenarioId, cropId, preds.join(',')],
    queryFn: () =>
      api.post<WizardPreview>('/api/v1/planning/tech-cards/wizard/preview', {
        scenario_id: scenarioId,
        crop_id: cropId,
        predecessor_crop_ids: preds,
      }),
    enabled: !!scenarioId && !!cropId,
    staleTime: 15_000,
    retry: false,
  });
}

export interface WizardCreateInput {
  seasonId: string;
  name: string;
  cropIds: string[];
  predecessorCropIds: string[];
  targetYieldCHa?: number | null;
  scenarioId?: string | null;
  applyStagePreset?: boolean;
}

export interface WizardCreatedCard {
  card_id: string;
  crop_id: string;
  name: string;
  auto_area_ha: number | null;
  fields_count: number | null;
  rows_matched: number | null;
  gaps: string[];
}

/** Мастер: N культур = N карт за один проход; с вариантом плана — сразу назначение. */
export function useCreateViaWizard() {
  const invalidate = useInvalidate();
  return useMutation<WizardCreatedCard[], Error, WizardCreateInput>({
    mutationFn: ({ seasonId, name, cropIds, predecessorCropIds, targetYieldCHa, scenarioId, applyStagePreset }) =>
      api.post<WizardCreatedCard[]>('/api/v1/planning/tech-cards/wizard', {
        season_id: seasonId,
        name: name.trim(),
        crop_ids: cropIds,
        predecessor_crop_ids: predecessorCropIds,
        target_yield_c_ha: targetYieldCHa ?? null,
        scenario_id: scenarioId ?? null,
        apply_stage_preset: applyStagePreset ?? true,
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

// ── Этапы карты (ADR-071 D3) ─────────────────────────────────────────────────

export function useAddStage(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardStage, Error, { name: string; notes?: string | null }>({
    mutationFn: (body) => api.post<TechCardStage>(`/api/v1/planning/tech-cards/${cardId}/stages`, body),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function usePatchStage(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardStage, Error, { stageId: string; name: string }>({
    mutationFn: ({ stageId, name }) =>
      api.patch<TechCardStage>(`/api/v1/planning/tech-cards/${cardId}/stages/${stageId}`, { name }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** Удаление этапа = удаление ГРУППИРОВКИ: строки остаются, stage_id уходит в NULL. */
export function useDeleteStage(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { stageId: string }>({
    mutationFn: ({ stageId }) => api.delete(`/api/v1/planning/tech-cards/${cardId}/stages/${stageId}`),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** Drag-порядок этапов — ПОЛНЫЙ список одной транзакцией (половина drag'а хуже нуля). */
export function useReorderStages(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardStage[], Error, { ids: string[] }>({
    mutationFn: ({ ids }) =>
      api.put<TechCardStage[]>(`/api/v1/planning/tech-cards/${cardId}/stages/order`, { ids }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export interface CloneCardInput {
  cardId: string;
  name: string;
  intoSeasonId?: string | null;
}

export function useCloneTechCard() {
  const invalidate = useInvalidate();
  return useMutation<TechCard, Error, CloneCardInput>({
    mutationFn: ({ cardId, name, intoSeasonId }) =>
      api.post<TechCard>(`/api/v1/planning/tech-cards/${cardId}/clone`, {
        name: name.trim(),
        ...(intoSeasonId ? { into_season_id: intoSeasonId } : {}),
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

// ── Строки карты ─────────────────────────────────────────────────────────────

/**
 * Тело создания строки. `company_op_type_id` обязателен: строка техкарты —
 * это ТИП работы хозяйства («Дискование»), а не свободный текст, иначе нормы
 * выработки и расценки не с чем сопоставить (они заданы на тип операции).
 */
export interface OperationInput {
  company_op_type_id: string;
  block_kind: BlockKind;
  name?: string | null;
  parent_operation_id?: string | null;
  /** Этап карты (ADR-071 D3). null = «без этапа». */
  stage_id?: string | null;
  /** Доля обрабатываемой площади, % (ADR-071 D5). */
  area_share_pct?: number;
  window_starts_on?: string | null;
  window_ends_on?: string | null;
  window_work_days?: number | null;
  multiplicity?: number;
  volume_basis?: TechCardOperation['volume_basis'];
  volume?: number | null;
  execution_kind?: TechCardOperation['execution_kind'];
  machinery_count?: number | null;
  throughput_norm?: number | null;
  /** Вид топлива — позиция секции «ГСМ» бюджетного справочника сезона. */
  fuel_item_id?: string | null;
  fuel_rate?: number | null;
  fuel_rate_basis?: string | null;
  fuel_ownership?: 'own' | 'provider' | null;
  fuel_overhead_coef?: number;
  haul_distance_km?: number | null;
  haul_capacity_t?: number | null;
  outsource_item_id?: string | null;
  outsource_rate_basis?: 'per_unit' | 'per_ton_km';
  outsource_share_pct?: number;
  /** НЗП вручную (ADR-070): `null` возвращает отнесение по календарю. */
  wip_override?: boolean | null;
  notes?: string | null;
}

/** Патч строки: тип работы не меняется (API его не принимает) — только состав. */
export type OperationPatch = Partial<Omit<OperationInput, 'company_op_type_id'>>;

export function useAddOperation(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardOperation, Error, OperationInput>({
    mutationFn: (body) =>
      api.post<TechCardOperation>(`/api/v1/planning/tech-cards/${cardId}/operations`, body),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function usePatchOperation(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardOperation, Error, { opId: string; patch: OperationPatch }>({
    mutationFn: ({ opId, patch }) =>
      api.patch<TechCardOperation>(
        `/api/v1/planning/tech-cards/${cardId}/operations/${opId}`,
        patch,
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useDeleteOperation(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { opId: string }>({
    mutationFn: ({ opId }) =>
      api.delete(`/api/v1/planning/tech-cards/${cardId}/operations/${opId}`),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/**
 * Материалы строки правятся НАБОРОМ целиком (PUT): в UI это одна таблица, и
 * «добавил один — удалил другой» обязано доехать одной транзакцией, иначе
 * половина правки останется на экране, а половина в базе.
 */
export interface MaterialInput {
  budget_item_id: string;
  rate: number;
  rate_basis: TechCardMaterial['rate_basis'];
  rate_unit_id?: string | null;
  /** Доля площади У МАТЕРИАЛА, % (ADR-071 D5): элита на 7 %, обычные на 93 %. */
  area_share_pct?: number;
  variety_id?: string | null;
  seeding_density?: number | null;
  seeding_density_unit_id?: string | null;
  seq?: number;
  notes?: string | null;
}

export function useSetMaterials(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardMaterial[], Error, { opId: string; materials: MaterialInput[] }>({
    mutationFn: ({ opId, materials }) =>
      api.put<TechCardMaterial[]>(
        `/api/v1/planning/tech-cards/${cardId}/operations/${opId}/materials`,
        materials.map((m, i) => ({ ...m, seq: m.seq ?? i })),
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/**
 * Редактор ФОТ строки (ADR-071 D11): набор ролей заменяется целиком, как и
 * материалы. Наличие хотя бы одной строки отключает старый путь расценки —
 * инвариант бэка против двойного счёта.
 */
export function useSetLabor(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardLaborRow[], Error, { opId: string; labor: TechCardLaborRow[] }>({
    mutationFn: ({ opId, labor }) =>
      api.put<TechCardLaborRow[]>(
        `/api/v1/planning/tech-cards/${cardId}/operations/${opId}/labor`,
        labor,
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** Прямые суммы строки по статьям (ADR-071 D5): «Пожарные мероприятия» эталона. */
export function useSetDirectCosts(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardDirectCost[], Error, { opId: string; directCosts: TechCardDirectCost[] }>({
    mutationFn: ({ opId, directCosts }) =>
      api.put<TechCardDirectCost[]>(
        `/api/v1/planning/tech-cards/${cardId}/operations/${opId}/direct-costs`,
        directCosts,
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/**
 * Экономика карты В КОНТЕКСТЕ варианта плана (ADR-071 D1): площадь — override
 * назначения либо авто по строкам плана; деньги строк — в абсолюте.
 */
export function useTechCardCost(cardId: string | null, scenarioId: string | null) {
  return useQuery<CardCost, Error>({
    queryKey: [...techCardKeys.all(), 'cost', cardId, scenarioId],
    queryFn: () =>
      api.get<CardCost>(
        `/api/v1/planning/tech-cards/${cardId}/cost?scenario_id=${scenarioId}`,
      ),
    enabled: !!cardId && !!scenarioId,
    staleTime: 30_000,
  });
}

/**
 * Карта в подборе под строку плана + ПОЧЕМУ она предложена (AK-770).
 *
 * `scope` отвечает «чья карта», `crop_match` — «на ту ли культуру». Без этих
 * двух полей экран показывал ровный список, половина которого молча отказывала
 * при назначении (карты соседних хозяйств), а карты «Подсолнечника» под
 * строкой «Подсолнечник масличный» не показывались вовсе.
 */
export interface TechCardCandidate extends TechCard {
  scope: 'farm' | 'company' | 'other_farm';
  farm_name: string | null;
  crop_match: 'exact' | 'family';
  crop_name: string | null;
  adopt_required: boolean;
  is_assigned: boolean;
}

/** Подбор карт под культуру строки плана — считает сервер, не фильтр на клиенте. */
export function useTechCardCandidates(scenarioId: string | null, cropId: string | null) {
  return useQuery<TechCardCandidate[], Error>({
    queryKey: [...techCardKeys.all(), 'candidates', scenarioId, cropId] as const,
    queryFn: () =>
      api.get<TechCardCandidate[]>(
        `/api/v1/planning/scenarios/${scenarioId}/tech-cards/candidates?crop_id=${cropId}`,
      ),
    enabled: !!scenarioId && !!cropId,
    staleTime: 30_000,
  });
}

export interface AdoptCardInput {
  scenarioId: string;
  techCardId: string;
  /**
   * Культура СТРОКИ ПЛАНА, под которую берут карту. Обязательна по смыслу:
   * подбор предлагает и карты «той же культуры, записанной иначе» (дубли
   * справочника), и без этого поля сервер вешал копию на культуру КАРТЫ —
   * строка плана оставалась непокрытой при зелёном тосте (прогон «Заречного»
   * 28.08: так тихо выпал 41 % площади хозяйства).
   */
  cropId?: string | null;
  areaHa?: number | null;
  targetYieldCHa?: number | null;
}

/**
 * «Взять карту другого хозяйства»: копия в своё хозяйство + назначение одним
 * запросом. Карта своего хозяйства или уровня компании копии не требует —
 * сервер в этом случае просто назначает её (`adopted: false`).
 */
export function useAdoptTechCard() {
  const invalidate = useInvalidate();
  return useMutation<{ adopted: boolean; tech_card_id: string }, Error, AdoptCardInput>({
    mutationFn: ({ scenarioId, techCardId, cropId, areaHa, targetYieldCHa }) =>
      api.post(`/api/v1/planning/scenarios/${scenarioId}/tech-cards/adopt`, {
        tech_card_id: techCardId,
        crop_id: cropId ?? null,
        area_ha: areaHa ?? null,
        target_yield_c_ha: targetYieldCHa ?? null,
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export interface AssignCardInput {
  scenarioId: string;
  techCardId: string;
  /** Культура строки плана — та же причина, что у `AdoptCardInput.cropId`. */
  cropId?: string | null;
  areaHa?: number | null;
  targetYieldCHa?: number | null;
}

/** Назначение варианта варианту плана (ADR-066 D2): карта не принадлежит плану. */
export function useAssignTechCard() {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, AssignCardInput>({
    mutationFn: ({ scenarioId, techCardId, cropId, areaHa, targetYieldCHa }) =>
      api.put(`/api/v1/planning/scenarios/${scenarioId}/tech-cards`, {
        tech_card_id: techCardId,
        crop_id: cropId ?? null,
        area_ha: areaHa ?? null,
        target_yield_c_ha: targetYieldCHa ?? null,
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useUnassignTechCard() {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { scenarioId: string; techCardId: string }>({
    mutationFn: ({ scenarioId, techCardId }) =>
      api.delete(`/api/v1/planning/scenarios/${scenarioId}/tech-cards/${techCardId}`),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export interface ScenarioAssignment {
  id: string;
  scenario_id: string;
  tech_card_id: string;
  crop_id: string;
  area_ha: number | null;
  target_yield_c_ha: number | null;
  tech_card_name: string;
  tech_card_status: TechCardStatus;
  cost?: CardCost;
}

export function useScenarioAssignments(scenarioId: string | null) {
  return useQuery<ScenarioAssignment[], Error>({
    queryKey: techCardKeys.assignments(scenarioId),
    queryFn: () =>
      api.get<ScenarioAssignment[]>(`/api/v1/planning/scenarios/${scenarioId}/tech-cards`),
    enabled: !!scenarioId,
    staleTime: 30_000,
  });
}

/** Варианты плана хозяйства на сезон — цель назначения (`/planning/scenarios`). */
export interface PlanVariant {
  id: string;
  farm_id: string;
  season_id: string;
  name: string;
  status: string;
  is_selected: boolean;
  /** AK-740 — «основной вариант»: суммируется в обзоре СПП кластера/компании. */
  is_primary: boolean;
  planned_area_ha: number;
}

export function usePlanVariants(farmId: string | null, seasonId: string | null) {
  return useQuery<PlanVariant[], Error>({
    queryKey: variantListKeys.list(farmId, seasonId),
    queryFn: () =>
      api.get<PlanVariant[]>(
        `/api/v1/planning/scenarios?farm_id=${farmId}&season_id=${seasonId}`,
      ),
    enabled: !!farmId && !!seasonId,
    staleTime: 30_000,
  });
}

/** Ответ создания варианта: сам вариант + мягкий хинт лимита (ADR-053, не 422). */
export interface PlanVariantCreated extends PlanVariant {
  warning?: string | null;
}

/**
 * «Создать вариант плана» (`POST /planning/scenarios`) — вход в планирование:
 * без варианта посевному плану, СПП и сводке показывать нечего.
 *
 * После создания протухает ОБЩИЙ список вариантов (`variantListKeys`) — его
 * читают и назначение техкарт, и СПП; тост успеха здесь, автовыбор нового
 * варианта — на вызывающем экране (у него адресная строка).
 */
export function useCreatePlanVariant() {
  const qc = useQueryClient();
  return useMutation<PlanVariantCreated, Error, { name: string; farmId: string; seasonId: string }>({
    mutationFn: ({ name, farmId, seasonId }) =>
      api.post<PlanVariantCreated>('/api/v1/planning/scenarios', {
        name,
        farm_id: farmId,
        season_id: seasonId,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: variantListKeys.all() });
      toast.success(`Вариант «${created.name}» заведён`);
      // Лимит вариантов мягкий (ADR-053): создание не блокируется, но человеку говорим.
      if (created.warning) toast.warning(created.warning);
    },
    // Отказ НЕ тостуем: с запретом одноимённых вариантов (founder 21.08) самый
    // частый отказ — 409 «имя занято», и место ему рядом с полем ввода в
    // открытом диалоге, а не всплывашкой поверх закрывшейся формы. Диалог
    // показывает текст сам (`CreateVariantButton`), тост дублировал бы его.
  });
}

/**
 * «Переименовать вариант плана» (`PATCH /planning/scenarios/{id}`).
 *
 * Требование founder (прод-UAT 21.08): вариант заводится как «Вариант 1», и
 * переименовать его из интерфейса было нельзя — имя, под которым хозяйство
 * обсуждает план, оказывалось выдано системой навсегда.
 *
 * Протухает ТРИ неймспейса, потому что название варианта видно из трёх разных
 * источников, и обновить только чип значило бы оставить старое имя в шапке
 * соседнего экрана:
 *   • `variantListKeys` — чипы переключателя (посевной план, СПП, назначение
 *     техкарт, выбор контекста расчёта);
 *   • `planVariantKeys` — заголовок «Вариант «X» · назначение техкарт» и
 *     «Сводка варианта «X»» приезжают в `summary.scenario.name`;
 *   • `cropStructureKeys` — СПП отдаёт `scenario_name` внутри агрегата.
 * Перезагрузка страницы после переименования — не решение, а признание, что
 * экран не знает своих потребителей.
 *
 * Отказ (409 «имя занято», 403, 422) НЕ глотаем тостом здесь: диалог показывает
 * его рядом с полем и НЕ закрывается — иначе «сохранил» выглядит как успех.
 */
export function useRenamePlanVariant() {
  const qc = useQueryClient();
  return useMutation<PlanVariant, Error, { scenarioId: string; name: string }>({
    mutationFn: ({ scenarioId, name }) =>
      api.patch<PlanVariant>(`/api/v1/planning/scenarios/${scenarioId}`, { name }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: variantListKeys.all() });
      void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
      void qc.invalidateQueries({ queryKey: cropStructureKeys.all() });
      toast.success(`Вариант переименован в «${updated.name}»`);
    },
  });
}

/**
 * «Сделать вариант основным» (`POST /planning/scenarios/{id}/primary`, AK-740) —
 * звёздочка в списке вариантов: этот вариант суммируется в обзоре СПП
 * кластера/компании.
 *
 * Право и протухание — ТЕ ЖЕ, что у переименования (ревью head-web #892 п.3-4):
 * ручка требует `season_plan:U` и отдаёт 409 на архивном варианте/при гонке
 * («основным только что стал другой»), а `is_primary` уехал в `ScenarioOut`
 * (`GET /planning/scenarios`) — список кормит переключатели и назначения
 * техкарт, и обзора СПП, оба обязаны увидеть новую звёздочку сразу.
 */
export function useSetPrimaryVariant() {
  const qc = useQueryClient();
  return useMutation<PlanVariant, Error, { scenarioId: string }>({
    mutationFn: ({ scenarioId }) => api.post<PlanVariant>(`/api/v1/planning/scenarios/${scenarioId}/primary`),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: variantListKeys.all() });
      void qc.invalidateQueries({ queryKey: sppOverviewKeys.all() });
      toast.success(`«${updated.name}» — основной вариант`);
    },
    onError: (err) => toast.error(describeApiError(err)),
  });
}

// ── Культуры ─────────────────────────────────────────────────────────────────

/**
 * Имена культур для шапки карты и группировки списка. Карта хранит только
 * `crop_id` (ADR-066 D8: всегда СИСТЕМНАЯ культура), поэтому имя резолвим
 * справочником — показывать пользователю uuid нельзя.
 *
 * Резолв ТОЧЕЧНЫЙ, по идентификаторам с экрана (`/reference/crops/{id}`), а не
 * первой страницей каталога: тянуть `?limit=500` значило молча показать
 * «культура без названия в справочнике» у тенанта, чей справочник длиннее
 * страницы — при том, что название есть (нитпик 3 вердикта head-web по #793).
 * Запросы дедуплицируются react-query по ключу, кэш на 10 минут общий с
 * остальными потребителями карточки культуры.
 */
export function useCropNames(cropIds: readonly (string | null | undefined)[]) {
  const ids = [...new Set(cropIds.filter((id): id is string => !!id))].sort();
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['reference', 'crop', id] as const,
      queryFn: () => api.get<CropLike>(`/api/v1/reference/crops/${id}`),
      staleTime: 10 * 60_000,
      // 404 (культуру убрали из справочника) — не повод трижды ретраить:
      // экран покажет честное «культура не найдена в справочнике».
      retry: false,
    })),
  });

  const names: Record<string, string> = {};
  ids.forEach((id, i) => {
    const crop = results[i]?.data;
    if (crop) names[id] = cropLabel(crop);
  });
  return {
    names,
    isLoading: results.some((r) => r.isLoading),
  };
}

/**
 * Масса 1000 семян культуры (справочник культур) — подсказка «из густоты
 * выходит столько-то кг/га» в диалоге сева.
 *
 * Ключ запроса тот же, что у `useCropNames` — карточку культуры экран уже
 * тянет ради названия, второй сетевой поход не нужен.
 */
export function useCropSeedMass(cropId: string | null) {
  const query = useQuery<CropLike & { thousand_seeds_weight_min_g?: number | null; thousand_seeds_weight_max_g?: number | null }, Error>({
    queryKey: ['reference', 'crop', cropId ?? 'none'] as const,
    queryFn: () => api.get(`/api/v1/reference/crops/${cropId}`),
    enabled: !!cropId,
    retry: false,
    staleTime: 10 * 60_000,
  });
  return {
    minG: query.data?.thousand_seeds_weight_min_g ?? null,
    maxG: query.data?.thousand_seeds_weight_max_g ?? null,
  };
}

// ── Подписи и форматирование ─────────────────────────────────────────────────

export const EXECUTION_TITLES: Record<TechCardOperation['execution_kind'], string> = {
  own: 'своя',
  outsourced: 'наём, без техники',
  hired_labor: 'наёмные работники',
};

const VOLUME_UNITS: Record<TechCardOperation['volume_basis'], string> = {
  per_ha: 'га',
  per_ton: 'т',
  fixed: 'шт',
};

const NUM = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return NUM.format(value);
}

/** «18 320 ₽/га». Ноль — честный ноль, а не прочерк: он значит «не посчиталось». */
export function formatPerHa(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${NUM.format(Math.round(value))} ₽/га`;
}

export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1_000_000) {
    return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value / 1_000_000)} млн ₽`;
  }
  return `${NUM.format(Math.round(value))} ₽`;
}

/** Дельта со знаком: «−1 390 ₽/га» / «+1 380 ₽/га» / «без изменений». */
export function formatDeltaPerHa(value: number): string {
  if (!Number.isFinite(value) || Math.round(value) === 0) return 'без изменений';
  const sign = value < 0 ? '−' : '+';
  return `${sign}${NUM.format(Math.abs(Math.round(value)))} ₽/га`;
}

export function formatVolume(op: TechCardOperation): string {
  if (op.volume === null || op.volume === undefined) return '—';
  return `${NUM.format(op.volume)} ${VOLUME_UNITS[op.volume_basis]}`;
}

/** «01.08.2026 — 20.08.2026» / «до 10.09.2026» / «срок не задан» — полная форма. */
export function formatWindow(op: TechCardOperation): string {
  const from = formatDate(op.window_starts_on);
  const to = formatDate(op.window_ends_on);
  if (from && to) return `${from} — ${to}`;
  if (to) return `до ${to}`;
  if (from) return `с ${from}`;
  return 'срок не задан';
}

/**
 * Компактное агроокно для таблицы: «01.08–20.08.26», «до 10.09.26».
 *
 * Агроном сверяет техкарту со своим экселем, где строки плотные; полная дата
 * в каждой строке раздувала таблицу и выталкивала правые колонки за
 * горизонтальный скролл. Полная форма остаётся в подсказке ячейки.
 */
export function formatWindowShort(op: TechCardOperation): string {
  // Окно, перешагивающее Новый год (осенний сев → весенняя подкормка), пишем
  // с годом у ОБЕИХ дат: «01.08.26–20.01.27». Иначе «01.08–20.01.27»
  // читается как «внутри 2027-го», а это осенние работы прошлого года.
  const crossYear =
    !!op.window_starts_on &&
    !!op.window_ends_on &&
    op.window_starts_on.slice(0, 4) !== op.window_ends_on.slice(0, 4);
  const from = shortDate(op.window_starts_on, crossYear);
  const to = shortDate(op.window_ends_on, true);
  if (from && to) return `${from}–${to}`;
  if (to) return `до ${to}`;
  if (from) return `с ${from}`;
  return '—';
}

function shortDate(iso: string | null, withYear = false): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return withYear ? `${d}.${m}.${y.slice(2)}` : `${d}.${m}`;
}

/** Кратность в таблице: «×1», «×0,2». Единица — самый частый случай. */
export function formatMultiplicity(value: number): string {
  return `×${NUM.format(value)}`;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Подпись базы нормы расхода: «га», «ч», «смену», «км», «100 км». */
const FUEL_BASIS_UNIT: Record<string, string> = {
  per_ha: 'га',
  per_hour: 'ч',
  per_shift: 'смену',
  // Транспорт (ADR-071 D6-d п.7). `per_100km` — запись из нормативов хозяйства
  // (книга нормативов ведёт КАМАЗ как «л/100 км», AK-731): строка своего
  // override'а так не хранит, но унаследованную норму показывает честно.
  per_km: 'км',
  per_100km: '100 км',
};

/** Расход топлива строки: «8 л/га», «12 л/ч», «0,45 л/км», «—». */
export function formatFuel(op: TechCardOperation): string {
  if (op.fuel_rate === null || op.fuel_rate === undefined) return '—';
  const unit = FUEL_BASIS_UNIT[op.fuel_rate_basis ?? 'per_ha'] ?? 'га';
  return `${NUM.format(op.fuel_rate)} л/${unit}`;
}

/** Материалы строки: «Балерина 1 л/га × 1 350 ₽». */
export function formatMaterial(material: TechCardMaterial): string {
  const name = material.budget_item_name ?? 'позиция без названия';
  const basis = material.rate_basis === 'per_ton' ? '/т' : material.rate_basis === 'per_unit' ? '' : '/га';
  const rate = `${NUM.format(material.rate)}${basis}`;
  // Цена — общий денежный формат (AK-704: два знака ВСЕГДА). `NUM` рядом
  // остаётся для норм и объёмов: у «1,5 л/га» копейки были бы шумом, а у
  // «1 350,00 ₽» их отсутствие ломает сходимость строк с итогом.
  const price = material.planned_price === null ? 'цены нет' : formatPrice(material.planned_price);
  return `${name} ${rate} × ${price}`;
}

/** Себестоимость тонны из ₽/га и урожайности (ц/га) — как считает бэк. */
export function costPerTon(cost: CardCost | null | undefined): number | null {
  if (!cost) return null;
  return cost.cost_per_ton;
}

/**
 * Текст отказа бэка. Как в бюджетном слое: 409 приходит объектом
 * `{code, message}` — общий `describeApiError` вернул бы объект, и React упал
 * бы на рендере.
 */
export function techCardErrorText(err: unknown): string {
  const detail = (err as { rawDetail?: { detail?: unknown } })?.rawDetail?.detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  if (typeof detail === 'string') return detail;
  return describeApiError(err);
}

// ── Производные для экрана ───────────────────────────────────────────────────

/** Строки блока в порядке макета: основная операция, под ней её свита. */
export function operationsOfBlock(
  operations: TechCardOperation[],
  kind: BlockKind,
): TechCardOperation[] {
  const inBlock = operations.filter((op) => (op.block_kind ?? 'other') === kind);
  const parents = inBlock.filter((op) => !op.parent_operation_id);
  const out: TechCardOperation[] = [];
  for (const parent of parents) {
    out.push(parent);
    out.push(...inBlock.filter((op) => op.parent_operation_id === parent.id));
  }
  // Строка-свита, чей родитель в другом блоке, иначе бы потерялась.
  for (const op of inBlock) if (!out.includes(op)) out.push(op);
  return out;
}

/** Какие блоки реально есть в карте — плитки рисуем только по ним. */
export function blocksOfCard(operations: TechCardOperation[]): BlockKind[] {
  const present = new Set(operations.map((op) => (op.block_kind ?? 'other') as BlockKind));
  return BLOCK_ORDER.filter((kind) => present.has(kind));
}

/** Кандидаты на замену блока: другие варианты той же культуры с этим блоком. */
export function replacementSources(
  cards: TechCard[],
  current: TechCard,
  kind: BlockKind,
): TechCard[] {
  return cards.filter(
    (c) =>
      c.id !== current.id &&
      c.crop_id === current.crop_id &&
      !c.archived_at &&
      // Список карт не отдаёт строки — берём те, у кого блок виден в разбивке
      // ₽/га по блокам (ключ есть только если строки блока в карте есть).
      c.cost?.by_block?.[kind] !== undefined,
  );
}
